import {
  faCalendar,
  faClock,
  faUser
} from '@fortawesome/free-regular-svg-icons';
import {
  faArrowLeft,
  faBan,
  faEdit,
  faRobot,
  faShare
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FalconComponentCard from 'components/common/FalconComponentCard';
import Flex from 'components/common/Flex';
import IconButton from 'components/common/IconButton';
import { useFormatDateTime } from 'hooks/useFormatDateTime';
import useNotification from 'hooks/useNotification';
import { useEffect, useState } from 'react';
import { Badge, Button, Card } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import KbArticlesService from 'services/KbArticles.service';

const ArticlePreview = () => {
  const { id } = useParams();
  const { showSuccessNotification, showErrorNotification } = useNotification();
  const formatDateTime = useFormatDateTime();

  const [article, setArticle] = useState({});
  const [isPublished, setIsPublished] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) findArticle();
  }, [id]);

  // Aplicar estilos Bootstrap aos elementos do artigo
  useEffect(() => {
    // Adicionar classes Bootstrap para os elementos HTML do conteúdo do artigo
    const articleContent = document.querySelector('.article-content');

    if (articleContent) {
      // Estilizar headings
      articleContent.querySelectorAll('h2').forEach(h2 => {
        h2.classList.add('h2', 'mt-4', 'mb-3', 'fw-bold');
      });

      articleContent.querySelectorAll('h3').forEach(h3 => {
        h3.classList.add('h3', 'mt-3', 'mb-2', 'fw-bold');
      });

      // Estilizar parágrafos
      articleContent.querySelectorAll('p').forEach(p => {
        p.classList.add('m-0', 'lh-lg');
      });

      // Estilizar listas
      articleContent.querySelectorAll('ul, ol').forEach(list => {
        list.classList.add('m-0', 'ps-4');
      });

      // Estilizar blocos de código
      articleContent.querySelectorAll('pre').forEach(pre => {
        pre.classList.add('bg-light', 'p-3', 'rounded', 'mb-3');
      });

      articleContent.querySelectorAll('code').forEach(code => {
        code.classList.add('text-dark');
      });

      // Estilizar imagens
      articleContent.querySelectorAll('img').forEach(img => {
        img.classList.add('img-fluid', 'my-3');
      });
    }
  }, [article.content]);

  useEffect(() => {
    if (article.status?.status === 'PUBLISHED') {
      setIsPublished(true);
    }
  }, [article.status]);

  const findArticle = async () => {
    setIsLoading(true);
    try {
      const response = await KbArticlesService.find(id);
      console.log('findArticle', response);
      if (response.id) setArticle(response);
    } catch (error) {
      console.error('Erro ao carregar artigo:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const publish = async e => {
    e.preventDefault();
    e.stopPropagation();
    const req = await KbArticlesService.publish(id);
    if (req.ok) {
      showSuccessNotification('Artigo publicado com sucesso');
      findArticle();
    } else {
      showErrorNotification(req.userMessage || 'Erro ao publicar o artigo');
    }
  };

  const unpublish = async e => {
    e.preventDefault();
    e.stopPropagation();

    const req = await KbArticlesService.unpublish(id);
    if (req.ok) {
      setIsPublished(false);
      showSuccessNotification('Artigo despublicado com sucesso');
      findArticle();
    } else {
      showErrorNotification(req.userMessage || 'Erro ao despublicar o artigo');
    }
  };

  return (
    <>
      <FalconComponentCard>
        <Card.Header className="ps-2 w-100">
          <Flex
            justifyContent={'between'}
            alignItems="center"
            className={'w-100'}
          >
            <Flex alignItems="center">
              <IconButton
                icon={faArrowLeft}
                variant="falcon-default"
                size="sm"
                className="ms-3 me-2 d-print-none"
                aria-label="Voltar"
                as={Link}
                to="/documents"
              />

              <h3 className="m-0">Visualização do Artigo</h3>
            </Flex>

            {/* Botões de ação */}
            <Flex
              justifyContent="end"
              alignItems={'center'}
              className="d-print-none"
            >
              {isLoading ? (
                <>
                  <Skeleton count={2} height={36} baseColor="#5E6E82" />
                </>
              ) : (
                <>
                  <Button
                    variant="falcon-default"
                    as={Link}
                    to={`/documents/articles/${id}/edit`}
                    className="me-2"
                  >
                    <FontAwesomeIcon icon={faEdit} className="me-1" /> Editar
                  </Button>

                  {isPublished ? (
                    <Button onClick={unpublish}>
                      <FontAwesomeIcon icon={faBan} className="me-1" />
                      Despublicar
                    </Button>
                  ) : (
                    <Button onClick={publish}>
                      <FontAwesomeIcon icon={faShare} className="me-1" />
                      Publicar
                    </Button>
                  )}
                </>
              )}
            </Flex>
          </Flex>
        </Card.Header>

        <FalconComponentCard.Body>
          {isLoading ? (
            <>
              {/* Skeleton do cabeçalho do artigo */}
              <div className="mb-4">
                <h1 className="fw-bold mb-3">
                  <Skeleton height={40} width="60%" baseColor="#5E6E82" />
                </h1>

                <div className="d-flex align-items-center mb-3">
                  <div className="me-3 text-800">
                    <small className="d-flex flex-wrap gap-3">
                      <span className="me-2">
                        <Skeleton width={120} height={16} baseColor="#5E6E82" />
                      </span>
                      <span className="me-2">
                        <Skeleton width={150} height={16} baseColor="#5E6E82" />
                      </span>
                      <span className="me-2">
                        <Skeleton width={160} height={16} baseColor="#5E6E82" />
                      </span>
                    </small>
                  </div>
                </div>

                <div className="mb-3 d-flex gap-2">
                  <Skeleton height={24} width={60} baseColor="#5E6E82" />
                  <Skeleton height={24} width={80} baseColor="#5E6E82" />
                  <Skeleton height={24} width={70} baseColor="#5E6E82" />
                </div>
              </div>

              <hr className="text-secondary" />

              {/* Skeleton do conteúdo do artigo */}
              <div className="article-content mb-4">
                <div className="mb-4">
                  <Skeleton count={3} height={20} baseColor="#5E6E82" className="mb-2" />
                  <Skeleton height={20} width="80%" baseColor="#5E6E82" className="mb-4" />

                  <Skeleton height={24} width="40%" baseColor="#5E6E82" className="mb-3" />
                  <Skeleton count={4} height={18} baseColor="#5E6E82" className="mb-2" />
                  <Skeleton height={18} width="60%" baseColor="#5E6E82" className="mb-4" />

                  <Skeleton height={120} baseColor="#5E6E82" className="mb-4" />

                  <Skeleton count={3} height={18} baseColor="#5E6E82" className="mb-2" />
                  <Skeleton height={18} width="70%" baseColor="#5E6E82" />
                </div>
              </div>

              {/* Skeleton dos botões de ação */}
              <div className="d-flex justify-content-end align-items-center d-print-none">
                <div className="me-2">
                  <Skeleton height={38} width={80} baseColor="#5E6E82" />
                </div>
                <Skeleton height={38} width={100} baseColor="#5E6E82" />
              </div>
            </>
          ) : (
            <>
              {/* Cabeçalho do artigo */}
              <div className="mb-4">
                <h1 className="fw-bold mb-3">{article.title}</h1>

                <div className="d-flex align-items-center mb-3">
                  <div className="me-3 text-800">
                    <small>
                      <span className="me-2">
                        <FontAwesomeIcon
                          icon={article.user ? faUser : faRobot}
                          size="sm"
                          className="me-1"
                        />
                        {article.user
                          ? article.user.firstName + ' ' + article.user.lastName
                          : 'Sistema'}
                      </span>

                      <span className="me-2">
                        <FontAwesomeIcon
                          icon={faCalendar}
                          size="sm"
                          className="me-1"
                        />
                        Criado em {formatDateTime(article.creationDate)}
                      </span>

                      <span className="me-2">
                        <FontAwesomeIcon
                          icon={faClock}
                          size="sm"
                          className="me-1"
                        />
                        Atualizado em {formatDateTime(article.lastUpdateDate)}
                      </span>

                      {isPublished && (
                        <>
                          <span className="me-2">
                            <FontAwesomeIcon
                              icon={'share'}
                              size="sm"
                              className="me-1"
                            />
                            Publicado por {article.publishedBy?.firstName}{' '}
                            {article.publishedBy?.lastName}
                          </span>
                          <span className="me-2">
                            <FontAwesomeIcon
                              icon={faCalendar}
                              size="sm"
                              className="me-1"
                            />
                            Publicado em {formatDateTime(article.lastUpdateDate)}
                          </span>
                        </>
                      )}
                    </small>
                  </div>
                </div>

                <div className="mb-3">
                  {article.tags
                    ? article.tags.map((tag, index) => (
                      <Badge
                        key={index}
                        bg="light"
                        text="dark"
                        className="me-1 mb-1 p-2"
                      >
                        {tag.name}
                      </Badge>
                    ))
                    : 'Nenhuma tag associada'}
                </div>
              </div>

              <hr className="text-secondary" />

              {/* Conteúdo do artigo */}
              <div className="article-content mb-4">
                <div
                  className="mb-4"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </div>

              {/* Botões de ação */}
              <Flex
                justifyContent="end"
                alignItems={'center'}
                className="d-print-none"
              >
                <Button
                  variant="falcon-default"
                  as={Link}
                  to={`/documents/articles/${id}/edit`}
                  className="me-2"
                >
                  <FontAwesomeIcon icon={faEdit} className="me-1" /> Editar
                </Button>

                {isPublished ? (
                  <Button onClick={unpublish}>
                    <FontAwesomeIcon icon={faBan} className="me-1" />
                    Despublicar
                  </Button>
                ) : (
                  <Button onClick={publish}>
                    <FontAwesomeIcon icon={faShare} className="me-1" />
                    Publicar
                  </Button>
                )}
              </Flex>
            </>
          )}
        </FalconComponentCard.Body>
      </FalconComponentCard>
    </>
  );
};

export default ArticlePreview;
